import { TextAvatar } from '@/shared/ui/components';
import { userIcon } from '@/shared/ui/assets';
import {
  DOUBLE_HYPHEN_BULLET,
  SUPPORT_REQUEST_ACTION,
  SUPPORT_REQUEST_ACTION_CONTENT,
  SUPPORT_REQUEST_STATUS_ENUM,
} from '@/shared/config';
import { SUPPORT_REQUEST_TYPE_ENUM } from '@/entities/support';
import { generateFullName, gettextSupportRequestAction } from '@/shared/lib';
import dayjs from 'dayjs';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import CommentInput from './commentInput';
import StudyList from './studyList';
import './style.scss';
import SupportTableStudy from './supportTableStudy';
import type { SupportRequestCommentProps } from './index.types';

const SupportRequestComment = ({
  isLoadedData = true,
  detailData,
  commentData,
  handleUpdateComment,
}: SupportRequestCommentProps) => {
  const { contents, category, issues, message, resolution, status } = detailData || {};

  /**
   * Renders the body of a support request's opening comment according to its category. Switches on category to show SupportTableStudy for ChangeStudyType, StudyList for ReportRequest, AbortStudy and ChangeStudyInformation, the message alone for WebPortalIssues and Other, and a comma-joined billing issue line for BillingQuestions, then appends the resolution block when a resolution exists.
   *
   * @param _comment - The opening comment record; unused by the rendering.
   * @returns The category-specific comment body with an optional resolution block.
   */
  const renderCommentOpen = (_comment?: any) => {
    const messageShow = message;
    const {
      ReportRequest,
      ChangeStudyType,
      AbortStudy,
      WebPortalIssues,
      BillingQuestions,
      Other,
      ChangeStudyInformation,
    } = SUPPORT_REQUEST_TYPE_ENUM;
    let contentJsx = null;
    switch (category) {
      case ChangeStudyType: {
        contentJsx = (
          <>
            <div className="title-table">{t`Study list`}</div>
            <SupportTableStudy data={contents || []} />
            <div className="description">{messageShow}</div>
          </>
        );
        break;
      }
      case ReportRequest:
      case AbortStudy:
      case ChangeStudyInformation: {
        contentJsx = (
          <>
            <div className="title-list">{t`Study ID:`}</div>
            <StudyList data={contents || []} />
            <div className="description">{messageShow}</div>
          </>
        );
        break;
      }
      case WebPortalIssues:
      case Other: {
        contentJsx = <div className="description">{messageShow}</div>;
        break;
      }
      case BillingQuestions: {
        contentJsx = (
          <>
            <div className="description">
              {t`Billing issue: `} {_.isEmpty(issues) ? DOUBLE_HYPHEN_BULLET : _.join(issues, ', ')}
            </div>
            <div className="description mt-2">{messageShow}</div>
          </>
        );
        break;
      }
      default: {
        break;
      }
    }

    return (
      <>
        {contentJsx}
        {!!resolution && (
          <div className="resolution">
            <div className="resolution-header">{t`Resolution`}</div>
            <div className="description description__resolution">
              {/* {gettext(resolution)} */}
              {resolution}
            </div>
          </div>
        )}
      </>
    );
  };

  /**
   * Builds the view model of one support request comment. Derives the author name via generateFullName and an MM/DD/YYYY HH:mm timestamp from createdAt, picks the content by action - the category-specific opening body for OPEN and plain text for COMMENT - and maps CLOSED and REOPEN to the matching SUPPORT_REQUEST_ACTION_CONTENT label, using the by-email variant when there is no author.
   *
   * @param comment - Comment record from the support request thread.
   * @returns Object holding the author name, timestamp, content node and action label.
   */
  const generateDataRender = (comment: any) => {
    const { createdBy, createdAt, action, content } = comment || {};
    const dataRender: any = {
      fullName: generateFullName(createdBy, ''),
      time: createdAt ? dayjs(createdAt).format('MM/DD/YYYY HH:mm') : DOUBLE_HYPHEN_BULLET,
      content: null,
      action: null,
    };

    const { OPEN, CLOSED, REOPEN, COMMENT } = SUPPORT_REQUEST_ACTION;

    dataRender.content = {
      [OPEN]: renderCommentOpen(comment),
      [COMMENT]: <div className="description">{content}</div>,
    }[action];

    dataRender.action = {
      [CLOSED]: _.isEmpty(createdBy)
        ? SUPPORT_REQUEST_ACTION_CONTENT.CLOSED_BY_EMAIL
        : SUPPORT_REQUEST_ACTION_CONTENT.CLOSED,
      [REOPEN]: SUPPORT_REQUEST_ACTION_CONTENT.REOPEN,
    }[action];

    return dataRender;
  };

  if (!isLoadedData) {
    return (
      <div className="support-request-comment">
        {_.map(_.range(4), (d) => (
          <div key={d} className="comment-content">
            <div className="part-user">
              <div className="avatar-skeleton">
                <Skeleton width={60} height={60} />
              </div>
              <div className="user-info">
                <div className="name">
                  <Skeleton count={1} width={100} />
                </div>
                <div className="time">
                  <Skeleton count={1} width={120} />
                </div>
              </div>
            </div>
            <div className="part-content">
              <div className="title-table">
                <Skeleton count={1} width="50%" />
              </div>
              <div className="description">
                <Skeleton count={5} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  /**
   * Renders one entry of the support request comment thread. Takes name, timestamp, action label and content from generateDataRender, shows a TextAvatar for a known author or a generic user icon for a system entry, translates the action label through gettextSupportRequestAction, and renders the content block only when present.
   *
   * @param comment - Comment record from the support request thread.
   * @returns The rendered comment entry.
   */
  const renderContentComment = (comment: any) => {
    const { createdBy } = comment || {};
    const dataRender = generateDataRender(comment);
    return (
      <>
        <div className="part-user">
          {_.isEmpty(createdBy) ? (
            <img alt="icon user" src={userIcon} />
          ) : (
            <TextAvatar data={createdBy} />
          )}
          <div className="user-info">
            <div className="name">
              {dataRender.fullName}
              {!!dataRender.action && (
                <>
                  {' '}
                  <span className="action">{gettextSupportRequestAction(dataRender.action)}</span>
                </>
              )}
            </div>
            <div className="time">{dataRender.time}</div>
          </div>
        </div>
        {!!dataRender.content && <div className="part-content">{dataRender.content}</div>}
      </>
    );
  };

  return (
    <div className="support-request-comment">
      {_.map(commentData, (item) => (
        <div key={item?.id} className="comment-content">
          {renderContentComment(item)}
        </div>
      ))}
      {status === SUPPORT_REQUEST_STATUS_ENUM.New && (
        <div className="comment-content">
          <CommentInput onSubmit={handleUpdateComment} />
        </div>
      )}
    </div>
  );
};

export default SupportRequestComment;
